import { useMemo, useRef } from "react";
import { useMutation } from "@tanstack/react-query";
import { AnimatePresence } from "motion/react";
import { Navigate, useNavigate } from "react-router-dom";
import { t } from "ttag";

import { EVENT_PREFIX } from "@/features/analytics/models";
import { trackBillingAddressStarted } from "@/features/analytics/trackers/billing-address-started";
import { trackCompletePurchaseClicked } from "@/features/analytics/trackers/complete-purchase-clicked";
import type { IntakeFormPayload } from "@/features/billing/api";
import {
  PayButton,
  PaymentDetails,
  PaymentErrorAlert,
} from "@/features/onboarding/components";
import {
  useCheckout,
  useCompleteOnboarding,
  useCreateUserForm,
  useHandlePaymentRedirect,
  useOnboardingUser,
  useTrackCheckoutInitiated,
} from "@/features/onboarding/hooks";
import { ONBOARDING_ROUTE } from "@/features/onboarding/router";
import {
  AddressFieldset,
  PaymentFieldset,
} from "@/features/onboarding/sections";
import { useOnboardingStore } from "@/features/onboarding/stores";
import { MissingClientSecretError } from "@/features/payments/utils";
import { OrderSummary } from "@/features/products/components";
import { LogoLoader, TermsAndConditionsField } from "@/shared/components";
import { PAYMENT_METHOD_TYPE, SUBSCRIPTION_VENDOR } from "@/shared/domains";
import { useBoolean } from "@/shared/hooks";
import {
  Checkbox,
  Collapse,
  errorToast,
  Form,
  Heading,
  Layout,
  SectionHeader,
  validateError,
  VerifiedCheckIcon,
} from "@/shared/ui";
import { ProviderCallModal } from "../components";
import { QUESTIONNAIRE_ROUTE } from "../router/constants";
import { useQuestionnaireStore } from "../stores";

export const Checkout = () => {
  const navigate = useNavigate();

  const { value: showPaymentFailedAlert, setTrue: openPaymentFailedAlert } =
    useBoolean(false);

  const { value: showProviderCallModal, setTrue: openProviderCallModal } =
    useBoolean(false);

  const billingAddressTracked = useRef(false);

  const goToAccountCreation = () => navigate(ONBOARDING_ROUTE.ACCOUNT_PASSWORD);

  const contactDetails = useOnboardingStore((state) => state.contactDetails);
  const selectedCategories = useOnboardingStore(
    (state) => state.selectedCategories,
  );
  const setContactDetails = useOnboardingStore(
    (state) => state.setContactDetails,
  );
  const checkoutDiscount = useOnboardingStore(
    (state) => state.checkoutDiscount,
  );
  const isCallCenter = useOnboardingStore((state) => state.isCallCenter);

  const intakeFormId = useQuestionnaireStore((store) => store.intakeFormId);
  const intakeAnswers = useQuestionnaireStore((store) => store.intakeAnswers);

  const intakeForm = useMemo<IntakeFormPayload | undefined>(() => {
    if (!intakeFormId || !intakeAnswers) {
      return undefined;
    }

    return {
      formId: intakeFormId,
      answers: Object.entries(intakeAnswers).map(([fieldId, value]) => ({
        fieldId,
        value: Array.isArray(value) ? value.join("\n") : String(value ?? ""),
      })),
    };
  }, [intakeFormId, intakeAnswers]);

  const user = useOnboardingUser();
  const form = useCreateUserForm();
  const isBillingSameAsShipping = form.watch("isBillingSameAsShipping");

  const { isVerifying, isRedirect } = useHandlePaymentRedirect({
    eventPrefix: EVENT_PREFIX.QUESTIONNAIRE,
  });

  const {
    card: { pay: cardPay },
    klarna: { pay: klarnaPay },
  } = useCheckout({
    klarnaReturnPath: QUESTIONNAIRE_ROUTE.CHECKOUT,
    intakeForm,
    eventPrefix: EVENT_PREFIX.QUESTIONNAIRE,
  });

  const completeOnboardingMutation = useCompleteOnboarding();

  const cardPayMutation = useMutation({
    mutationFn: cardPay,
    onSuccess: (data) => {
      // WhiteCoat sync visits require a phone consultation: inform the patient
      // before they finish creating their account.
      if (data?.needsSync && data.vendor === SUBSCRIPTION_VENDOR.WHITE_COAT) {
        openProviderCallModal();
        return;
      }

      // OpenLoop sync sets the subscription flow status to require scheduling
      // an initial appointment, which the home surfaces after login.
      goToAccountCreation();
    },
    onError: (error) => {
      const validatedError = validateError(error);
      if (
        validatedError &&
        validatedError.response.data.error.code !== "stripe_card_error"
      ) {
        openPaymentFailedAlert();
      }
    },
  });

  const klarnaPayMutation = useMutation({
    mutationFn: klarnaPay,
    onError: (error) => {
      if (error instanceof MissingClientSecretError) {
        if (!user) {
          return;
        }
        return completeOnboardingMutation.mutate({
          code: user.code,
          state: user.state,
          shipping: user.shipping,
          phoneNumber: user.phoneNumber,
          isCallCenter,
        });
      }
      errorToast(error.message);
    },
  });

  useTrackCheckoutInitiated({
    enabled: !isRedirect,
    eventPrefix: EVENT_PREFIX.QUESTIONNAIRE,
  });

  if (!user || !selectedCategories.length) {
    return <Navigate to={QUESTIONNAIRE_ROUTE.PRODUCT} replace />;
  }

  const handlePay = form.handleSubmit((formData) => {
    if (contactDetails) {
      setContactDetails({
        ...contactDetails,
        shipping: formData.shipping,
        billing: formData.billing ?? formData.shipping,
        state: formData.state,
      });
    }

    if (formData.paymentMethodType === PAYMENT_METHOD_TYPE.CARD) {
      trackCompletePurchaseClicked({
        user,
        selectedCategories: useOnboardingStore.getState().selectedCategories,
        paymentMethod: formData.paymentMethodType,
        eventPrefix: EVENT_PREFIX.QUESTIONNAIRE,
      });
      return cardPayMutation.mutate();
    }

    if (formData.paymentMethodType === PAYMENT_METHOD_TYPE.KLARNA) {
      return klarnaPayMutation.mutate();
    }
  });

  return (
    <Layout.Content>
      <SectionHeader
        title={t`Almost there! Pay for your order`}
        description={t`Fill out your details and payment method to complete your purchase.`}
      />

      <Form.Provider {...form}>
        <div className="grid w-full grid-cols-1 items-start gap-6 md:grid-cols-[1fr_400px] md:gap-10">
          <div className="flex flex-col gap-4 md:col-start-2 md:row-start-1">
            <OrderSummary
              categories={selectedCategories}
              checkoutDiscount={checkoutDiscount ?? undefined}
              sticky={false}
            />
            <PaymentDetails className="hidden md:block" />
          </div>

          <form
            onSubmit={handlePay}
            className="flex flex-col gap-6 md:col-start-1 md:row-start-1"
          >
            <fieldset
              className="flex flex-col gap-4"
              onFocus={() => {
                if (billingAddressTracked.current) {
                  return;
                }
                billingAddressTracked.current = true;
                trackBillingAddressStarted({
                  userId: user.email,
                  eventPrefix: EVENT_PREFIX.QUESTIONNAIRE,
                });
              }}
            >
              <Heading size="small" level={3} color="primary" weight="medium">
                {t`Shipping information`}
              </Heading>

              <AddressFieldset form={form} type="shipping" />

              <Form.Field
                control={form.control}
                name="isBillingSameAsShipping"
                render={({ field: { value, onChange, ...rest } }) => (
                  <Form.Item>
                    <Form.Control>
                      <Checkbox.Item
                        checked={value}
                        onCheckedChange={(checked) => {
                          onChange(checked);
                          form.clearErrors("billing");
                          form.setValue("billing", undefined);
                        }}
                        {...rest}
                      >
                        <Checkbox.Label>
                          {t`Billing address is the same as shipping`}
                        </Checkbox.Label>
                      </Checkbox.Item>
                    </Form.Control>
                  </Form.Item>
                )}
              />

              <AnimatePresence>
                {!isBillingSameAsShipping && (
                  <Collapse key="billing-address" className="col-span-full">
                    <AddressFieldset
                      form={form}
                      type="billing"
                      title={t`Billing address`}
                    />
                  </Collapse>
                )}
              </AnimatePresence>
            </fieldset>

            <PaymentFieldset
              headingAlign="left"
              onClick={handlePay}
              eventPrefix={EVENT_PREFIX.QUESTIONNAIRE}
            />

            <div className="flex flex-col gap-4">
              <TermsAndConditionsField control={form.control} />

              <div className="flex flex-col items-center gap-3 [&>button]:w-full [&>button]:max-w-md">
                <PayButton
                  label={t`Pay with Card`}
                  disabled={
                    cardPayMutation.isPending || klarnaPayMutation.isPending
                  }
                />

                <div className="flex items-center gap-1.5 text-sm">
                  <VerifiedCheckIcon className="size-5 text-green-500" />
                  <span>
                    <strong>{t`HSA/FSA`}</strong> {t`Eligible`}
                  </span>
                </div>
              </div>
            </div>

            <PaymentDetails className="md:hidden" />
          </form>
        </div>
      </Form.Provider>

      <LogoLoader
        show={
          cardPayMutation.isPending ||
          completeOnboardingMutation.isPending ||
          isVerifying
        }
        fullscreen
      >
        {t`We are processing your payment, just give us a moment. You'll be able to create your LevelsRx account shortly!`}
      </LogoLoader>

      <LogoLoader show={klarnaPayMutation.isPending} fullscreen>
        {t`Almost there — we're redirecting you to Klarna to complete your payment`}
      </LogoLoader>

      <PaymentErrorAlert open={showPaymentFailedAlert} />

      <ProviderCallModal
        open={showProviderCallModal}
        onContinue={goToAccountCreation}
      />
    </Layout.Content>
  );
};
